import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Radio, Pause, Play, Clock, Volume2, VolumeX, Target as TgIcon, Trophy, Maximize2, Minimize2, GripVertical } from "lucide-react";
import { fmtNum } from "@/lib/helpers";
import SaleDetailDialog from "@/components/SaleDetailDialog";
import { RevenueFeed, TvStrip, StarCard } from "@/components/RevenueFeed";
import { MonthLeaderCard, WeekLeaderCard, MgrMonthLeaderCard, MgrWeekLeaderCard } from "@/components/Badges";
import { ChampionCelebration } from "@/components/ChampionCelebration";
import { Celebration, playVictory } from "@/components/Celebration";
import { fmtTL } from "@/components/RevenueDialog";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}sn önce`;
  if (diff < 3600) return `${Math.floor(diff/60)}dk önce`;
  if (diff < 86400) return `${Math.floor(diff/3600)}sa önce`;
  return `${Math.floor(diff/86400)}gün önce`;
}

// Short "ding" synthesized via Web Audio API — no asset file needed
function playPing(audioCtxRef, gain = 0.08) {
  try {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new Ctx();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1320, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.14);
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    osc.connect(g).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.42);
  } catch {}
}

export default function LivePanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [paused, setPaused] = useState(false);
  const [sound, setSound] = useState(true);
  const [tvMode, setTvMode] = useState(false);
  const [order, setOrder] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [revFilter, setRevFilter] = useState("");
  const [threshold, setThreshold] = useState(500);
  const [celebQueue, setCelebQueue] = useState([]);
  const thresholdRef = useRef(500);
  const soundRef = useRef(true);
  const prevRevIds = useRef(null);
  thresholdRef.current = threshold;
  soundRef.current = sound;
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/sales") || m.path.startsWith("/api/target"));
  const [flash, setFlash] = useState({});
  const [selected, setSelected] = useState(null);
  const prevIds = useRef(new Set());
  const audioCtxRef = useRef(null);
  const firstLoad = useRef(true);
  const timer = useRef();

  const load = async () => {
    try {
      const { data } = await http.get(`/sales/live?per_branch=12`);
      setData(data);
      const nextIds = new Set();
      data.branches.forEach((b) => b.sales.forEach((s) => nextIds.add(s.id)));
      const added = {};
      nextIds.forEach((id) => { if (!prevIds.current.has(id)) added[id] = true; });
      if (!firstLoad.current && Object.keys(added).length > 0) {
        setFlash((p) => ({ ...p, ...added }));
        if (sound) playPing(audioCtxRef);
        setTimeout(() => {
          setFlash((p) => {
            const next = { ...p };
            Object.keys(added).forEach((k) => delete next[k]);
            return next;
          });
        }, 3000);
      }
      prevIds.current = nextIds;
      firstLoad.current = false;
      if (data.revenue) {
        const ids = new Set(data.revenue.feed.map((x) => x.id));
        if (prevRevIds.current) {
          const big = data.revenue.feed.filter((x) => !prevRevIds.current.has(x.id) && x.amount > 0 && x.amount >= thresholdRef.current);
          if (big.length) setCelebQueue((q) => [...q, ...big.reverse()]);
        }
        prevRevIds.current = ids;
      }
    } catch {}
  };

  useEffect(() => {
    load();
    timer.current = setInterval(() => { if (!paused) load(); }, 15000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line
  }, [paused, sound]);

  useEffect(() => { if (tick && !paused) load(); }, [tick]); // eslint-disable-line

  useEffect(() => {
    http.get("/me/preferences").then((r) => {
      setOrder(r.data.live_branch_order || []);
      if (r.data.celebrate_threshold != null) setThreshold(r.data.celebrate_threshold);
    }).catch(() => {});
  }, []);

  const saveThreshold = (v) => {
    const n = Math.max(0, Number(v) || 0);
    setThreshold(n);
    http.put("/me/preferences", { celebrate_threshold: n }).catch(() => {});
  };
  const [champs, setChamps] = useState([]);
  const champ = champs[0] || null;
  const setChamp = (c) => setChamps(c ? [c] : []);
  const closeChamp = useCallback(() => setChamps((q) => q.slice(1)), []);
  useEffect(() => {
    if (!tvMode) { setChamp(null); return; }
    const tr = new Date(Date.now() + 3 * 3600 * 1000);
    if (tr.getUTCDate() !== 1 && !window.__forceChampion) return;
    const py = tr.getUTCMonth() === 0 ? tr.getUTCFullYear() - 1 : tr.getUTCFullYear();
    const pm = tr.getUTCMonth() === 0 ? 12 : tr.getUTCMonth();
    http.get("/badges").then((r) => {
      const hit = (x) => x.year === py && x.month === pm;
      const q = [r.data.champions.find(hit), (r.data.mgr_champions || []).find(hit)].filter(Boolean);
      if (q.length) setChamps(q);
    }).catch(() => {});
  }, [tvMode]); // eslint-disable-line
  useEffect(() => { if (champ && soundRef.current) playVictory(audioCtxRef); }, [champ]); // eslint-disable-line
  const celebItem = celebQueue[0] || null;
  useEffect(() => { if (celebItem && soundRef.current) playVictory(audioCtxRef); }, [celebItem]);
  const nextCeleb = useCallback(() => setCelebQueue((q) => q.slice(1)), []);

  const sortBranches = (list) => {
    const pos = (id) => { const i = order.indexOf(id); return i === -1 ? 9999 : i; };
    return [...list].sort((a, b) => pos(a.branch_id) - pos(b.branch_id));
  };

  const dropOn = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = sortBranches(data.branches).map((b) => b.branch_id).filter((id) => id !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId);
    setOrder(ids);
    setDragId(null);
    http.put("/me/preferences", { live_branch_order: ids }).catch(() => {});
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {}
  };

  const enterTv = async () => {
    setTvMode(true);
    await toggleFullscreen();
  };
  const exitTv = async () => {
    setTvMode(false);
    if (document.fullscreenElement) await document.exitFullscreen();
  };

  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) setTvMode(false); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  if (!data) return <div className="text-slate-400 p-10">Yükleniyor...</div>;

  const totalToday = data.branches.reduce((s, b) => s + b.today.count, 0);
  const totalTarget = data.branches.reduce((s, b) => s + (b.daily_target || 0), 0);
  const totalTargetSold = data.branches.reduce((s, b) => s + (b.today_target_qty || 0), 0);
  const totalPct = totalTarget > 0 ? Math.round((totalTargetSold / totalTarget) * 100) : 0;
  const n = data.branches.length;
  const tvColN = n <= 1 ? 1 : n <= 4 ? n : n <= 6 ? 3 : 4;
  const tvCols = ["grid-cols-1", "grid-cols-1", "grid-cols-2", "grid-cols-3", "grid-cols-4"][tvColN];
  const leader = [...data.branches].sort((a, b) => (b.realization_pct || 0) - (a.realization_pct || 0))[0];

  const view = (
    <div className={tvMode ? "force-dark fixed inset-0 z-[45] bg-slate-950 p-4 flex flex-col gap-3 overflow-hidden" : "space-y-4"} data-testid="live-root">
      <div className="flex flex-wrap items-end justify-between gap-3 animate-fade-up">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-3">
            <span className="relative inline-flex h-3 w-3">
              <span className={`absolute inline-flex h-full w-full rounded-full ${paused ? "bg-slate-500" : "bg-red-500 animate-ping"} opacity-75`} />
              <span className={`relative inline-flex h-3 w-3 rounded-full ${paused ? "bg-slate-500" : "bg-red-500"}`} />
            </span>
            Canlı Panel
          </h1>
          <p className="text-slate-400 text-sm mt-1 flex items-center gap-2">
            <Clock size={12} /> {new Date(data.server_time).toLocaleTimeString("tr-TR")}
            <span className="text-slate-600">·</span>
            <span>{paused ? "Duraklatıldı" : "Anlık güncelleniyor"}</span>
            <span className="text-slate-600">·</span>
            <span className="flex items-center gap-1"><GripVertical size={12} /> Sıralamak için şubeleri sürükleyin</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2">
            <div className="text-[10px] uppercase font-mono text-emerald-200">Bugün</div>
            <div className="font-display text-xl font-bold text-emerald-300">{fmtNum(totalToday)} işlem</div>
          </div>
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2">
            <div className="text-[10px] uppercase font-mono text-amber-200">Hedef Adet (Bugün)</div>
            <div className="font-display text-xl font-bold text-amber-300" data-testid="live-total-target">{fmtNum(totalTargetSold)} / {fmtNum(totalTarget)}</div>
          </div>
          <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-2">
            <div className="text-[10px] uppercase font-mono text-blue-200">Gerçekleşme</div>
            <div className="font-display text-xl font-bold text-blue-300">%{totalPct}</div>
          </div>
          {data.revenue && (
            <label className="rounded-xl border border-amber-500/30 bg-slate-900/60 px-3 py-1.5 flex flex-col" title="Bu tutar ve üstündeki satışlarda kutlama gösterilir">
              <span className="text-[10px] uppercase font-mono text-amber-200">Kutlama Eşiği</span>
              <span className="flex items-center gap-1">
                <input type="number" min="0" defaultValue={threshold} key={threshold} onBlur={(e) => saveThreshold(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                       className="w-20 bg-transparent font-display text-lg font-bold text-amber-300 outline-none" data-testid="celebrate-threshold" />
                <span className="text-amber-300 font-bold">₺</span>
              </span>
            </label>
          )}
          <button onClick={() => { setSound((v) => !v); if (!sound) playPing(audioCtxRef); }}
                  data-testid="live-sound-btn"
                  className="px-3 py-2 rounded-xl border border-slate-700 bg-slate-900/60 hover:bg-slate-800 flex items-center gap-2 text-sm" title="Ses">
            {sound ? <Volume2 size={14} className="text-emerald-300" /> : <VolumeX size={14} className="text-slate-500" />}
          </button>
          <button onClick={() => setPaused((v) => !v)} data-testid="live-pause-btn"
                  className="px-4 py-2 rounded-xl border border-slate-700 bg-slate-900/60 hover:bg-slate-800 flex items-center gap-2 text-sm">
            {paused ? <><Play size={14} /> Devam</> : <><Pause size={14} /> Duraklat</>}
          </button>
          <button onClick={tvMode ? exitTv : enterTv} data-testid="live-tv-btn"
                  className="px-4 py-2 rounded-xl border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 flex items-center gap-2 text-sm font-semibold">
            {tvMode ? <><Minimize2 size={14} /> TV Kapat</> : <><Maximize2 size={14} /> TV Modu</>}
          </button>
        </div>
      </div>

      {leader && leader.realization_pct > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent px-4 py-2 flex items-center gap-2 animate-fade-up delay-100">
          <Trophy size={16} className="text-amber-400" />
          <span className="text-sm font-semibold text-amber-200">Günün Lideri:</span>
          <span className="font-display font-bold">{leader.branch_name}</span>
          <span className="text-xs text-slate-400">· %{leader.realization_pct} gerçekleşme · {fmtNum(leader.today.points)} puan</span>
        </div>
      )}

      {data.ticker.length > 0 && (
        <div className="relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900/80 h-10 flex items-center">
          <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center pl-3 pr-4 bg-slate-900 shadow-[12px_0_16px_rgb(var(--c-slate-900)/0.95)]">
            <Radio size={14} className="text-red-400 animate-pulse mr-2" />
            <span className="text-xs font-mono text-slate-300 uppercase tracking-widest">AKIŞ</span>
          </div>
          <div className="flex gap-6 whitespace-nowrap animate-ticker pl-32" data-testid="live-ticker">
            {[...data.ticker, ...data.ticker].map((s, i) => (
              <span key={i} className="text-sm flex items-center gap-2">
                <span className="text-slate-500 font-mono text-[10px]">{s.product_sku}</span>
                <span className="text-slate-300 font-semibold">{s.product_name}</span>
                <span className="text-slate-500">×{s.quantity}</span>
                <span className="text-amber-300 font-bold">+{fmtNum(s.points)}p</span>
                <span className="text-slate-500">·</span>
                <span className="text-slate-400 text-xs">{s.user_name}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className={tvMode ? "flex gap-4 flex-1 min-h-0" : "flex flex-col xl:flex-row gap-4"}>
      <div className={`flex-1 min-w-0 grid gap-4 ${tvMode ? `${tvCols} flex-1 min-h-0` : (data.branches.length <= 2 ? "lg:grid-cols-2" : data.branches.length <= 4 ? "md:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4")}`} data-testid="live-grid" style={tvMode ? { gridAutoRows: "minmax(0, 1fr)" } : undefined}>
        {sortBranches(data.branches).map((b, i) => {
          const pct = b.realization_pct || 0;
          const barColor = pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-blue-500";
          return (
            <Card key={b.branch_id} draggable onDragStart={() => setDragId(b.branch_id)} onDragEnd={() => setDragId(null)}
                  onDragOver={(e) => e.preventDefault()} onDrop={() => dropOn(b.branch_id)} data-testid={`live-card-${b.branch_code}`}
                  className={`border-slate-800 bg-slate-900/60 glass overflow-hidden flex flex-col cursor-grab active:cursor-grabbing transition-[opacity,transform] ${dragId === b.branch_id ? "opacity-40 scale-[0.98]" : ""} ${tvMode ? "h-full min-h-0" : "h-[600px]"} animate-fade-up`}
                  style={{ animationDelay: `${i * 60}ms` }}>
              {/* Branch header */}
              <div className="relative p-4 border-b border-slate-800 bg-gradient-to-br from-slate-800/60 to-transparent">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-1.5">
                    <GripVertical size={16} className="text-slate-600 mt-1 shrink-0" />
                    <div>
                    <div className="text-[10px] font-mono text-blue-300 uppercase tracking-widest">{b.branch_code}</div>
                    <div className="font-display font-bold text-lg">{b.branch_name}</div>
                    {b.city && <div className="text-xs text-slate-500">{b.city}</div>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-mono text-slate-500">BUGÜN</div>
                    <div className="font-display text-2xl font-bold text-emerald-300" data-testid={`live-today-count-${b.branch_code}`}>{fmtNum(b.today.count)}</div>
                    <div className="text-xs text-amber-300 font-semibold">{fmtNum(b.today.points)}p</div>
                  </div>
                </div>
                {/* Daily target bar */}
                {b.daily_target > 0 ? (
                  <div data-testid={`live-target-${b.branch_code}`}>
                    <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
                      <span className="flex items-center gap-1"><TgIcon size={10} /> Bugün / Günlük Gereken: {fmtNum(b.today_target_qty)}/{fmtNum(b.daily_target)} adet · Ay HGO %{b.month_hgo}</span>
                      <span className={`font-bold ${pct >= 100 ? "text-emerald-300" : pct >= 50 ? "text-amber-300" : "text-slate-300"}`}>%{pct}</span>
                    </div>
                    <div className="relative h-2 rounded-full bg-slate-800 overflow-hidden">
                      <div className={`absolute inset-y-0 left-0 ${barColor} transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
                      {pct >= 100 && <div className="absolute inset-0 bg-emerald-400/20 animate-pulse" />}
                    </div>
                  </div>
                ) : (
                  <div className="text-[10px] text-slate-600 italic">Günlük hedef tanımlanmamış</div>
                )}
                {tvMode && <TvStrip b={b} />}
                {data.revenue && (
                  <div className="mt-2 flex items-center justify-between text-xs rounded-lg bg-emerald-500/5 border border-emerald-500/20 px-2.5 py-1.5" data-testid={`live-revenue-${b.branch_code}`}>
                    <span className="text-slate-400">Bugün <b className="text-emerald-300">{fmtTL(data.revenue.by_branch[b.branch_id]?.today)}</b></span>
                    <span className="text-slate-400">Ay <b className="text-slate-100">{fmtTL(data.revenue.by_branch[b.branch_id]?.month)}</b></span>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid={`live-branch-${b.branch_code}`}>
                {b.sales.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-600 text-xs">Henüz satış yok</div>
                ) : (
                  b.sales.map((s) => {
                    const idStatus = s.id_card_status || (s.id_card_front_path && s.id_card_back_path ? "beklemede" : "eksik");
                    const idDotCls = idStatus === "tamam" ? "bg-emerald-400" : idStatus === "sorunlu" ? "bg-red-400" : idStatus === "beklemede" ? "bg-amber-400" : "bg-slate-500";
                    return (
                      <button
                        key={s.id}
                        onClick={() => setSelected(s.id)}
                        data-testid={`live-sale-${s.id}`}
                        className={`w-full text-left rounded-lg px-3 py-2.5 border transition-all ${
                          flash[s.id]
                            ? "border-emerald-400/60 bg-emerald-500/15 shadow-[0_4px_20px_rgba(16,185,129,0.3)] animate-pulse"
                            : "border-slate-800 bg-slate-900/60 hover:border-slate-700 hover:bg-slate-800/60"
                        }`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                              <span className={`inline-block h-1.5 w-1.5 rounded-full ${idDotCls}`} title={`Kimlik: ${idStatus}`} />
                              {flash[s.id] && <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"/>}
                              {s.product_name}
                            </div>
                            <div className="text-[10px] font-mono text-slate-500 truncate">{s.product_sku}</div>
                            <div className="text-[11px] text-slate-400 truncate mt-0.5">{s.user_name}</div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-bold text-amber-300 font-display">+{fmtNum(s.points)}</div>
                            <div className="text-[10px] text-slate-500">×{s.quantity}</div>
                          </div>
                        </div>
                        <div className="mt-1 text-[10px] text-slate-500 font-mono flex items-center gap-1">
                          <Clock size={9} /> {timeAgo(s.created_at)}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </Card>
          );
        })}
      </div>
      {!data.revenue && (data.star || data.month_leader || data.week_leader || data.month_mgr_leader || data.week_mgr_leader) && (
        <div className={`${tvMode ? "w-64 overflow-y-auto min-h-0" : "w-full xl:w-64"} shrink-0`} data-testid="star-column"><MonthLeaderCard leader={data.month_leader} tv={tvMode} /><WeekLeaderCard leader={data.week_leader} tv={tvMode} /><MgrMonthLeaderCard leader={data.month_mgr_leader} tv={tvMode} /><MgrWeekLeaderCard leader={data.week_mgr_leader} tv={tvMode} /><StarCard star={data.star} tv={tvMode} /></div>
      )}
      {data.revenue && <RevenueFeed rev={data.revenue} star={data.star} leader={data.month_leader} weekLeader={data.week_leader} mgrLeader={data.month_mgr_leader} mgrWeekLeader={data.week_mgr_leader} branches={sortBranches(data.branches)} filter={revFilter} setFilter={setRevFilter} tv={tvMode} />}
      </div>

      <Celebration item={celebItem} onDone={nextCeleb} />
      <ChampionCelebration champ={champ} onDone={closeChamp} />
      <SaleDetailDialog saleId={selected} onClose={() => setSelected(null)} onUpdated={load} />

      <style>{`
        @keyframes ticker-move { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-ticker { animation: ticker-move 60s linear infinite; }
      `}</style>
    </div>
  );
  return tvMode ? createPortal(view, document.body) : view;
}
